import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Search, Users as UsersIcon, Loader2, MoreVertical, Ban, RotateCcw, KeyRound, Trash2, Shield, MailPlus, BellRing,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const PAGE_SIZE = 25;

const roleBadge = (r) => ({
    professional: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
    employer: "bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30",
    recruiter: "bg-violet-500/15 text-violet-700 dark:text-violet-400 border-violet-500/30",
    hiring_team: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
}[r] || "bg-slate-500/15 text-slate-600 dark:text-slate-400 border-slate-500/30");

const statusBadge = (s) => ({
    active: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
    suspended: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
    deleted: "bg-destructive/10 text-destructive border-destructive/30",
}[s] || "bg-slate-500/15 text-slate-600 dark:text-slate-400 border-slate-500/30");

export default function UserManagement() {
    const [searchParams] = useSearchParams();
    const initialRole = searchParams.get("role") || "any";
    const initialStatus = searchParams.get("status") || "any";
    const [data, setData] = useState({ results: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [role, setRole] = useState(initialRole);
    const [status, setStatus] = useState(initialStatus);
    const [page, setPage] = useState(1);

    const [actionDialog, setActionDialog] = useState(null); // { user, action }
    const [reason, setReason] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const params = { page, page_size: PAGE_SIZE };
            if (q) params.q = q;
            if (role !== "any") params.role = role;
            if (status !== "any") params.status = status;
            const { data } = await api.get("/admin/users", { params });
            setData(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [q, role, status, page]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { setPage(1); }, [q, role, status]);

    const runAction = async () => {
        if (!actionDialog) return;
        try {
            setSubmitting(true);
            const { user, action } = actionDialog;
            if (action === "delete") {
                await api.delete(`/admin/users/${user.user_id}`);
            } else {
                const body = { action, reason };
                if (action === "reset_password") body.new_password = newPassword;
                await api.post(`/admin/users/${user.user_id}/action`, body);
            }
            toast.success("Done.");
            setActionDialog(null);
            setReason("");
            setNewPassword("");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

    return (
        <div data-testid="admin-user-management">
            <div className="mb-6">
                <p className="overline">Command Center</p>
                <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                    User Management
                </h1>
                <p className="text-sm text-muted-foreground mt-1">Search + moderate every user account on the platform.</p>
            </div>

            <div className="card-soft p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="relative md:col-span-2">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        value={q} onChange={(e) => setQ(e.target.value)}
                        placeholder="Search by name, email, or company…"
                        data-testid="users-search"
                        className="pl-9 h-10"
                    />
                </div>
                <Select value={role} onValueChange={setRole}>
                    <SelectTrigger className="h-10" data-testid="users-filter-role"><SelectValue placeholder="Any role" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="any">Any role</SelectItem>
                        <SelectItem value="professional">Professional</SelectItem>
                        <SelectItem value="employer">Employer</SelectItem>
                        <SelectItem value="recruiter">Recruiter</SelectItem>
                        <SelectItem value="hiring_team">Hiring team</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="h-10" data-testid="users-filter-status"><SelectValue placeholder="Any status" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="any">Any status</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="suspended">Suspended</SelectItem>
                        <SelectItem value="deleted">Deleted</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
            ) : data.results.length === 0 ? (
                <div className="card-soft p-10 text-center">
                    <UsersIcon size={22} className="mx-auto text-muted-foreground" />
                    <div className="mt-2 font-heading font-semibold">No users found.</div>
                </div>
            ) : (
                <div className="card-soft overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-border text-left text-[11px] uppercase text-muted-foreground">
                                    <th className="p-3 font-semibold">User</th>
                                    <th className="p-3 font-semibold">Role</th>
                                    <th className="p-3 font-semibold">Status</th>
                                    <th className="p-3 font-semibold">Signed up</th>
                                    <th className="p-3 font-semibold">Last seen</th>
                                    <th className="p-3 font-semibold text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.results.map((u) => (
                                    <tr key={u.user_id} className="border-b border-border/60 last:border-0 hover:bg-muted/30" data-testid={`user-row-${u.user_id}`}>
                                        <td className="p-3">
                                            <div className="font-semibold text-foreground truncate max-w-[280px]">{u.name || "—"}</div>
                                            <div className="text-xs text-muted-foreground truncate max-w-[280px]">{u.email}</div>
                                            {u.company_name && <div className="text-[11px] text-muted-foreground">{u.company_name}</div>}
                                        </td>
                                        <td className="p-3">
                                            <Badge className={`text-[10px] border ${roleBadge(u.role)}`}>{u.role}</Badge>
                                        </td>
                                        <td className="p-3">
                                            <Badge className={`text-[10px] border ${statusBadge(u.status)}`}>{u.status || "active"}</Badge>
                                            {u.suspension_reason && (
                                                <div className="text-[10px] text-muted-foreground mt-0.5 max-w-[200px] truncate" title={u.suspension_reason}>{u.suspension_reason}</div>
                                            )}
                                        </td>
                                        <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">
                                            {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                                        </td>
                                        <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">
                                            {u.last_seen_at ? new Date(u.last_seen_at).toLocaleDateString() : "—"}
                                        </td>
                                        <td className="p-3 text-right">
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <button className="btn-outline text-xs px-2 py-1 inline-flex items-center gap-1" data-testid={`user-actions-${u.user_id}`}>
                                                        <MoreVertical size={14} />
                                                    </button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    {u.status !== "suspended" && u.status !== "deleted" && (
                                                        <DropdownMenuItem onClick={() => { setActionDialog({ user: u, action: "suspend" }); setReason(""); }} data-testid={`user-suspend-${u.user_id}`}>
                                                            <Ban size={13} className="mr-2 text-amber-500" /> Suspend
                                                        </DropdownMenuItem>
                                                    )}
                                                    {u.status === "suspended" && (
                                                        <DropdownMenuItem onClick={() => { setActionDialog({ user: u, action: "reactivate" }); setReason(""); }} data-testid={`user-reactivate-${u.user_id}`}>
                                                            <RotateCcw size={13} className="mr-2 text-emerald-500" /> Reactivate
                                                        </DropdownMenuItem>
                                                    )}
                                                    {u.status !== "deleted" && (
                                                        <>
                                                            {u.role === "professional" && (
                                                                <DropdownMenuItem onClick={() => { setActionDialog({ user: u, action: "send_passport_reminder" }); setReason(""); }} data-testid={`user-send-passport-reminder-${u.user_id}`}>
                                                                    <BellRing size={13} className="mr-2 text-violet-500" /> Send passport reminder
                                                                </DropdownMenuItem>
                                                            )}
                                                            <DropdownMenuItem onClick={() => { setActionDialog({ user: u, action: "send_reset_link" }); setReason(""); }} data-testid={`user-send-reset-link-${u.user_id}`}>
                                                                <MailPlus size={13} className="mr-2 text-emerald-500" /> Send reset link
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem onClick={() => { setActionDialog({ user: u, action: "reset_password" }); setNewPassword(""); }} data-testid={`user-reset-${u.user_id}`}>
                                                                <KeyRound size={13} className="mr-2 text-sky-500" /> Set new password
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem onClick={() => setActionDialog({ user: u, action: "delete" })} data-testid={`user-delete-${u.user_id}`}>
                                                                <Trash2 size={13} className="mr-2 text-rose-500" /> Delete account
                                                            </DropdownMenuItem>
                                                        </>
                                                    )}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                            <span className="text-xs text-muted-foreground">Page {page} of {totalPages} · {data.total} users</span>
                            <div className="flex items-center gap-2">
                                <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="btn-outline text-xs px-3 py-1 disabled:opacity-50" data-testid="users-prev">Prev</button>
                                <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="btn-outline text-xs px-3 py-1 disabled:opacity-50" data-testid="users-next">Next</button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <Dialog open={!!actionDialog} onOpenChange={(o) => !o && setActionDialog(null)}>
                <DialogContent data-testid="user-action-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Shield size={16} className="text-emerald-500" /> Confirm action
                        </DialogTitle>
                        <DialogDescription>
                            {actionDialog?.action === "suspend" && <>Suspend <span className="font-semibold">{actionDialog.user.email}</span>? Their active sessions will be revoked.</>}
                            {actionDialog?.action === "reactivate" && <>Reactivate <span className="font-semibold">{actionDialog.user.email}</span>.</>}
                            {actionDialog?.action === "send_passport_reminder" && <>Email <span className="font-semibold">{actionDialog.user.email}</span> a friendly nudge to finish their Talent Passport. Uses their current completion % from the passport row.</>}
                            {actionDialog?.action === "send_reset_link" && <>Email a one-time password-reset link to <span className="font-semibold">{actionDialog.user.email}</span>. The link expires in 30 minutes. Sessions stay active until the user completes the reset.</>}
                            {actionDialog?.action === "reset_password" && <>Set a new password for <span className="font-semibold">{actionDialog.user.email}</span>. Their sessions will be revoked.</>}
                            {actionDialog?.action === "delete" && <>Soft-delete <span className="font-semibold">{actionDialog.user.email}</span>. Account marked deleted; records retained for audit.</>}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-2 space-y-3">
                        {(actionDialog?.action === "suspend" || actionDialog?.action === "reactivate" || actionDialog?.action === "send_reset_link" || actionDialog?.action === "send_passport_reminder") && (
                            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason / note (optional)" data-testid="user-action-reason" />
                        )}
                        {actionDialog?.action === "reset_password" && (
                            <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="New password (min 8 chars)" data-testid="user-action-newpassword" />
                        )}
                    </div>
                    <DialogFooter>
                        <button onClick={() => setActionDialog(null)} className="btn-outline text-sm px-4 py-2" data-testid="user-action-cancel">Cancel</button>
                        <button
                            onClick={runAction}
                            disabled={submitting || (actionDialog?.action === "reset_password" && newPassword.length < 8)}
                            className={`text-sm px-4 py-2 rounded-full inline-flex items-center gap-1 disabled:opacity-60 ${actionDialog?.action === "delete" ? "bg-destructive text-white hover:opacity-90" : "btn-green"}`}
                            data-testid="user-action-confirm"
                        >
                            {submitting ? <Loader2 size={13} className="animate-spin" /> : null} Confirm
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
